import React, { useState, useEffect } from 'react';
import DashboardWrapper from '../components/DashboardWrapper';
import { useAuth } from '../context/AuthContext';
import { User, Heart, Flag, LogOut, Edit } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import ProductCard from '../components/ProductCard';
import FollowingProductsTable from '../components/FollowingProductsTable';

const PersonalSpacePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [favorites, setFavorites] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'favorites') {
      loadFavorites();
    } else if (activeTab === 'following') {
      loadFollowing();
    }
  }, [activeTab]);

  const loadFavorites = async () => {
    setLoading(true);
    try {
      const response = await api.get('/favorites?type=favori');
      setFavorites(response.data);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFollowing = async () => {
    setLoading(true);
    try {
      const response = await api.get('/favorites?type=a_suivre');
      setFollowing(response.data);
    } catch (error) {
      console.error('Error loading following:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const tabs = [
    { id: 'profile', label: 'Profil', icon: User },
    { id: 'favorites', label: 'Ma Wishlist', icon: Heart },
    { id: 'following', label: 'À suivre', icon: Flag }
  ];

  return (
    <DashboardWrapper>
      {/* Container unifié 1200px - cohérent avec le design system */}
      <div 
        className="space-y-6" 
        data-testid="personal-space-page"
        style={{
          maxWidth: '1200px',
          marginLeft: 'auto',
          marginRight: 'auto',
          paddingLeft: '16px',
          paddingRight: '16px',
          paddingTop: '32px',
          paddingBottom: '32px',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-heading font-bold mb-2">Mon Espace Personnel</h1>
            <p className="text-muted-foreground">
              Gérez votre profil, votre Wishlist et vos produits à suivre
            </p>
          </div>
          <Button 
            variant="destructive" 
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Déconnexion
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex gap-4">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 pb-4 px-2 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary font-semibold'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="min-h-[400px]">
          {activeTab === 'profile' && (
            <div className="bg-white rounded-lg border p-6 max-w-2xl" data-testid="profile-section">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-heading font-semibold">Informations du Profil</h2>
                <Link to="/dashboard/profil">
                  <Button variant="outline" size="sm">
                    <Edit className="mr-2 h-4 w-4" />
                    Modifier
                  </Button>
                </Link>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Nom du magasin</label>
                    <p className="text-base font-semibold">{user?.nom_magasin}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="text-base">{user?.email}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Nom</label>
                    <p className="text-base">{user?.prenom_utilisateur} {user?.nom_utilisateur}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Ville</label>
                    <p className="text-base">{user?.ville}</p>
                  </div>
                </div>
                
                {user?.telephone && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Téléphone</label>
                    <p className="text-base">{user.telephone}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  {user?.surface_magasin && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Surface</label>
                      <p className="text-base font-semibold">{user.surface_magasin} m²</p>
                    </div>
                  )}
                  {user?.nb_salaries && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Salariés</label>
                      <p className="text-base font-semibold">{user.nb_salaries}</p>
                    </div>
                  )}
                  {user?.zone_implantation && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Zone</label>
                      <p className="text-base">{user.zone_implantation}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'favorites' && (
            <div data-testid="favorites-section">
              <h2 className="text-2xl font-heading font-semibold mb-6">
                Ma Wishlist ({favorites.length})
              </h2>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : favorites.length === 0 ? (
                <div className="bg-white rounded-lg border p-12 text-center">
                  <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">Aucun produit dans votre Wishlist</p>
                  <Link to="/dashboard/nouveautes">
                    <Button>Découvrir les produits</Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {favorites.map((fav) => fav.product && (
                    <ProductCard key={fav.product.id} product={fav.product} showTag={true} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'following' && (
            <div data-testid="following-section">
              <h2 className="text-2xl font-heading font-semibold mb-6">
                À suivre ({following.length})
              </h2>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : following.length === 0 ? (
                <div className="bg-white rounded-lg border p-12 text-center">
                  <Flag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">Aucun produit dans votre liste "À suivre"</p>
                  <Link to="/dashboard/precommandes">
                    <Button>Parcourir les pré-commandes</Button>
                  </Link>
                </div>
              ) : (
                <FollowingProductsTable 
                  products={following} 
                  loading={loading}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardWrapper>
  );
};

export default PersonalSpacePage;
